/* eslint-disable import/no-duplicates */
import React from 'react';

import { useTheme } from 'styled-components/macro';
import { TikTok, Plus, Sync, Search } from '@profitfy/pakkun-icons';
import { formatDistanceToNow } from 'date-fns';
import pt from 'date-fns/locale/pt-BR';
import { ETextSize } from '@domain/enums/components/EText';
import { ESynchronizationType } from '@domain/enums/hooks/ESynchronization';
import { useTikTokCrendentials } from '@helpers/hooks/common/integrations/marketing/tiktok/useTikTokCrendentials';
import { useSynchronization } from '@helpers/hooks/common/useSynchronization';
import { useDebounce } from '@helpers/hooks/useDebounce';
import { useDate } from '@helpers/hooks/useDate';

import AddNewProfileModal from '../AddNewProfileModal';

import * as S from './styles';

const Header: React.FC = () => {
  const theme = useTheme();

  const { utcToZonedTime } = useDate();
  const { currentSynchronizations } = useSynchronization();

  const {
    syncAllAdSenseAccounts,
    handleAdAccountSearchName,
    mutateAdSenseCredentials,
  } = useTikTokCrendentials();

  const [lastTimeUpdated] = React.useState<Date>(new Date());
  const [, setUpdatedDate] = React.useState<Date>(new Date());
  const [adAccountSearchName, setAdAccountSearchName] = React.useState<string>('');
  const [isAddNewProfileModalOpen, setIsAuthenticateModalOpen] = React.useState<boolean>(false);

  const handleAddNewProfileModalOpen = React.useCallback(() => {
    setIsAuthenticateModalOpen(currentState => !currentState);
  }, []);

  const handleSyncDataButtonClick = React.useCallback(async () => {
    await mutateAdSenseCredentials();
    syncAllAdSenseAccounts(undefined);
  }, [mutateAdSenseCredentials, syncAllAdSenseAccounts]);

  const onSearchInputChange = React.useCallback(event => {
    setAdAccountSearchName(event.target.value);
  }, []);

  const isSyncing = Boolean(
    currentSynchronizations?.synchronizations.find(synchronization => {
      if (
        synchronization.type === ESynchronizationType.AD_SENSE_ACCOUNTS ||
        synchronization.type === ESynchronizationType.AD_SENSE_CAMPAIGNS
      ) {
        return true;
      }

      return false;
    }),
  );

  const debouncedAdAccountSearchName = useDebounce(adAccountSearchName, 700);

  React.useEffect(() => {
    handleAdAccountSearchName(debouncedAdAccountSearchName);
  }, [debouncedAdAccountSearchName, handleAdAccountSearchName]);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setUpdatedDate(new Date());
    }, 60000);

    return () => clearInterval(interval);
  }, [lastTimeUpdated]);

  return (
    <S.Wrapper>
      <S.HeadingWrapper>
        <S.Heading>Integração com o TikTok</S.Heading>
        <S.Text size={ETextSize.B5}>
          Conecte perfis do TikTok para contabilizar seus gastos com anúncios.
        </S.Text>
      </S.HeadingWrapper>

      <S.Body>
        <S.AddNewProfileButton onClick={handleAddNewProfileModalOpen}>
          <TikTok size={22} />
          Adicionar perfil do TikTok
          <S.VerticalRule />
          <Plus size={20} color={theme.colors.gray[1]} />
        </S.AddNewProfileButton>

        <S.SyncAndInputWrapper>
          <S.LastUpdatedText size={ETextSize.B5}>
            Atualizado há <br />
            {`${formatDistanceToNow(utcToZonedTime(lastTimeUpdated), {
              locale: pt,
            })}`}
          </S.LastUpdatedText>

          <S.SyncDataButton onClick={handleSyncDataButtonClick} disabled={isSyncing}>
            <Sync size={17} color={theme.colors.gray[1]} />
          </S.SyncDataButton>

          <S.Input
            type="text"
            prepend={() => <Search size={20} color={theme.colors.gray[3]} />}
            onChange={onSearchInputChange}
          />
        </S.SyncAndInputWrapper>
      </S.Body>

      <AddNewProfileModal isOpen={isAddNewProfileModalOpen} toggle={handleAddNewProfileModalOpen} />
    </S.Wrapper>
  );
};

export default Header;
